import type { BalanceData, Enemy, LevelSim, SimEvent } from "@highmarch/domain";
import { SimEventKind, wyrmArchetype } from "@highmarch/domain";
import { WYRM_LOOK } from "@highmarch/app";
import type { RareDef } from "./rareApi";

/**
 * The Starfall Wyrm (M40, docs/RARES.md "The Starfall Wyrm") as the presentation reads it: the sim's API (domain
 * sim/wyrm.ts — the events WyrmSpawned … WyrmReleased, `sim.wyrm`, `sim.wyrmRun`, `sim.wyrmChoiceOpen`, the archetype
 * flagged `legend`) plus its look (the three sparkle colours, the starfield night's sky) in one place for the views.
 */

/** The Wyrm's look: a RareDef (so the rare machinery — shine, world sparkles, bar paint — takes it) and a third colour. */
export interface WyrmDef extends RareDef {
  /** The third sparkle colour: cyan, down the tail (the art's opal runs rose-gold → lilac → cyan). */
  readonly sparkC: number;
}

/** Lilac (app WYRM_LOOK.sparkle), rose-gold and cyan: the scale sheen of the model, head to tail. */
export const WYRM_COLOURS = { lilac: WYRM_LOOK.sparkle, rose: 0xffc9a8, cyan: 0x8ff2ff, white: 0xfff6ff } as const;

/** The Wyrm's look with its name from balance (one model, every level). */
export function wyrmDef(B: BalanceData): WyrmDef {
  const w = wyrmArchetype(B);
  return {
    id: "wyrm", chapter: 0, name: w >= 0 ? B.enemies[w].name : "Starfall Wyrm",
    sparkA: WYRM_COLOURS.lilac, sparkB: WYRM_COLOURS.rose, sparkC: WYRM_COLOURS.cyan,
    // the model is the star: a soft sheen, glow slots under the night's bloom, the opal kept off white (M40 review)
    // (and no lilac wash, a faint sheen: the wings' own cyan → violet → rose → gold must read through the night)
    sky: 0x3b2f86, shine: 0.16, albedo: 0.9, glow: 0.75, wash: 0,
  };
}

/** Whether an archetype is the Wyrm (`legend: true` in enemies.json). */
export function isWyrmArchetype(B: BalanceData, archetype: number): boolean { return B.enemies[archetype]?.legend === true; }

/** Whether `e` is the Wyrm (any appearance of it). */
export function isWyrm(sim: LevelSim, e: Enemy): boolean { return isWyrmArchetype(sim.balance, e.archetype); }

/** Whether enemy `id` is (or was, this tick) the Wyrm. */
export function isWyrmEnemy(sim: LevelSim, id: number): boolean {
  const e = sim.enemySlots.find((x) => x.id === id);
  return !!e && isWyrm(sim, e);
}

/** What the presentation knows of the living Wyrm (`sim.wyrm`). */
export interface WyrmNow { readonly enemyId: number; readonly attempt: number; readonly startedTick: number }

/** The Wyrm on the road now, or null. */
export function wyrmNow(sim: LevelSim): WyrmNow | null {
  const w = sim.wyrm;
  return w ? { enemyId: w.enemyId, attempt: w.attempts, startedTick: w.startedTick } : null;
}

/**
 * Whether the open rally offer (`offer.rare`) is the Wyrm's, not a rare's: both deal the same three offers, and the
 * latest of the two to appear owns the rally (they never stand on the field together).
 */
export function rallyIsWyrm(sim: LevelSim): boolean {
  const w = sim.wyrmRun, r = sim.rareRun;
  if (sim.currentOffer?.rare !== true || w === null) return false;
  return r === null || r.startedTick <= w.startedTick;
}

/** The choice is up: the Wyrm flew off and the battle waits on the Commander (never once the battle is over). */
export function wyrmChoiceUp(sim: LevelSim): boolean { return sim.wyrmChoiceOpen && !sim.isOver; }

/** The HP the next try brings it back with (what it fled with), and its full HP — for the choice sheet. */
export function wyrmHpLeft(sim: LevelSim): { readonly left: number; readonly max: number } | null {
  const r = sim.wyrmRun;
  return r ? { left: Math.max(1, Math.floor(r.hpLeft.toNumber())), max: Math.max(1, Math.floor(r.hp.toNumber())) } : null;
}

// -----------------------------------------------------------------------------------------------------------------
// The feed: what happened to the Wyrm this tick, from the sim's events
// -----------------------------------------------------------------------------------------------------------------

export type WyrmSignal =
  | { readonly kind: "spawned"; readonly enemyId: number; readonly attempt: number }
  | { readonly kind: "killed"; readonly enemyId: number; readonly attempt: number }
  | { readonly kind: "fled"; readonly enemyId: number; readonly attempt: number }
  | { readonly kind: "retried"; readonly attempt: number; readonly hp: number }
  | { readonly kind: "released"; readonly attempts: number };

/**
 * Reads the tick's events from `from` on (payloads: domain sim/events.ts): WyrmSpawned / WyrmKilled / WyrmFled (a =
 * enemy id, b = attempt, c = archetype), WyrmRetried (a = the new attempt, b = its HP), WyrmReleased (a = attempts).
 */
export function readWyrmSignals(sim: LevelSim, from: number, out: WyrmSignal[] = []): WyrmSignal[] {
  const events: readonly SimEvent[] = sim.events.all();
  for (let i = from; i < events.length; i++) {
    const ev = events[i];
    switch (ev.kind) {
      case SimEventKind.WyrmSpawned: out.push({ kind: "spawned", enemyId: ev.a, attempt: ev.b }); break;
      case SimEventKind.WyrmKilled: out.push({ kind: "killed", enemyId: ev.a, attempt: ev.b }); break;
      case SimEventKind.WyrmFled: out.push({ kind: "fled", enemyId: ev.a, attempt: ev.b }); break;
      case SimEventKind.WyrmRetried: out.push({ kind: "retried", attempt: ev.a, hp: ev.b }); break;
      case SimEventKind.WyrmReleased: out.push({ kind: "released", attempts: ev.a }); break;
      default: break;
    }
  }
  return out;
}
